import { buildApiUrl } from "@/lib/app-config";
import type { CreateSearchRequest, ErrorResponse, SearchResponse, UiError } from "@/lib/types";

export class ApiError extends Error {
  status: number;
  errorCode?: string;

  constructor({ message, status, errorCode }: UiError) {
    super(message);
    this.name = "ApiError";
    this.status = status ?? 500;
    this.errorCode = errorCode;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    if (!response.ok) {
      throw new ApiError({ message: `Request failed with status ${response.status}`, status: response.status });
    }

    return undefined as T;
  }

  const data = (await response.json()) as T | ErrorResponse;
  if (!response.ok) {
    const error = data as ErrorResponse;
    throw new ApiError({
      message: error.message || `Request failed with status ${response.status}`,
      status: response.status,
      errorCode: error.error_code,
    });
  }

  return data as T;
}

async function request<T>(input: string, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  return parseResponse<T>(response);
}

export async function createSearch(payload: CreateSearchRequest): Promise<SearchResponse> {
  return request<SearchResponse>(buildApiUrl("/api/searches"), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getSearch(searchId: string): Promise<SearchResponse> {
  return request<SearchResponse>(buildApiUrl(`/api/searches/${encodeURIComponent(searchId)}`));
}

export function toUiError(error: unknown): UiError {
  if (error instanceof ApiError) {
    return { message: error.message, errorCode: error.errorCode, status: error.status };
  }

  if (error instanceof Error) {
    return { message: error.message };
  }

  return { message: "Something went wrong while talking to the search API." };
}
